import { useCallback, useEffect, useState } from "react";
import { apiFetch, useApp } from "../../context/AppContext";
import { Button, Select, Input } from "./UI";
import CollapsibleSettingsCard from "./CollapsibleSettingsCard";
import { Bell, Clock } from "lucide-react";

export default function BreachSettingsPanel({ apiBase = "/tenant/settings" }) {
  const { showNotification } = useApp();
  const [reminderInterval, setReminderInterval] = useState(60);
  const [customValue, setCustomValue] = useState("");
  const [customUnit, setCustomUnit] = useState("minutes");
  const [isCustom, setIsCustom] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${apiBase}/breach`);
      if (!res.ok) throw new Error("Failed to load breach settings");
      const data = await res.json();
      const val = data.reminder_interval_minutes ?? 60;

      const standardVals = [15, 30, 60, 120, 180, 240, 480, 720, 1440];
      if (standardVals.includes(val)) {
        setReminderInterval(val);
        setIsCustom(false);
      } else {
        setReminderInterval("custom");
        setIsCustom(true);
        if (val % 60 === 0) {
          setCustomValue(String(val / 60));
          setCustomUnit("hours");
        } else {
          setCustomValue(String(val));
          setCustomUnit("minutes");
        }
      }
      setUpdatedAt(data.updated_at ? new Date(data.updated_at).toLocaleString("en-US", { month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "numeric", second: "numeric", hour12: true }).replace(" at ", ", ") : null);
    } catch (e) {
      showNotification(e.message || "Failed to load breach settings", "error");
    } finally {
      setLoading(false);
    }
  }, [apiBase, showNotification]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSelectChange = (e) => {
    const val = e.target.value;
    setReminderInterval(val);
    if (val === "custom") {
      setIsCustom(true);
      if (!customValue) {
        setCustomValue("15");
        setCustomUnit("minutes");
      }
    } else {
      setIsCustom(false);
    }
  };

  const handleSave = async () => {
    let finalValue = Number(reminderInterval);
    if (isCustom) {
      const parsed = parseInt(customValue, 10);
      if (isNaN(parsed) || parsed < 1) {
        showNotification("Please enter a positive value", "error");
        return;
      }

      if (customUnit === "hours") {
        if (parsed > 168) {
          showNotification("Maximum interval is 168 hours (7 days)", "error");
          return;
        }
        finalValue = parsed * 60;
      } else {
        if (parsed > 10080) {
          showNotification("Maximum interval is 10080 minutes (7 days)", "error");
          return;
        }
        finalValue = parsed;
      }
    }

    setSaving(true);
    try {
      const res = await apiFetch(`${apiBase}/breach`, {
        method: "PATCH",
        body: JSON.stringify({
          reminder_interval_minutes: finalValue,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Save failed");
      }
      const data = await res.json();
      const val = data.reminder_interval_minutes;

      const standardVals = [15, 30, 60, 120, 180, 240, 480, 720, 1440];
      if (standardVals.includes(val)) {
        setReminderInterval(val);
        setIsCustom(false);
      } else {
        setReminderInterval("custom");
        setIsCustom(true);
        if (val % 60 === 0) {
          setCustomValue(String(val / 60));
          setCustomUnit("hours");
        } else {
          setCustomValue(String(val));
          setCustomUnit("minutes");
        }
      }
      setUpdatedAt(data.updated_at ? new Date(data.updated_at).toLocaleString("en-US", { month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "numeric", second: "numeric", hour12: true }).replace(" at ", ", ") : null);
      showNotification("Breach settings saved successfully", "success");
    } catch (e) {
      showNotification(e.message || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const options = [
    { value: 15, label: "15 minutes" },
    { value: 30, label: "30 minutes" },
    { value: 60, label: "1 hour (Default)" },
    { value: 120, label: "2 hours" },
    { value: 180, label: "3 hours" },
    { value: 240, label: "4 hours" },
    { value: 480, label: "8 hours" },
    { value: 720, label: "12 hours" },
    { value: 1440, label: "24 hours (1 Day)" },
  ];

  return (
    <CollapsibleSettingsCard
      tourId="tour-tenant-settings-breach"
      title="Breach Notification Settings"
      description="Configure the interval at which email alerts and reminders are sent to reporters for unresolved breach incidents."
      icon={<Bell size={18} className="text-white animate-pulse" />}
      iconClassName="bg-gradient-to-br from-amber-500 to-orange-600"
    >
      {loading ? (
        <div className="animate-pulse space-y-3">
          <div className="h-10 bg-slate-100 rounded-lg" />
          <div className="h-10 bg-slate-100 rounded-lg w-1/4" />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
            <div className="space-y-4">
              <Select
                label="Breach Alert Reminder Interval"
                value={reminderInterval}
                onChange={handleSelectChange}
              >
                {options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
                <option value="custom">Custom...</option>
              </Select>

              {isCustom && (
                <div className="flex gap-3">
                  <div className="flex-1">
                    <Input
                      label="Custom Value"
                      type="number"
                      min="1"
                      value={customValue}
                      onChange={(e) => setCustomValue(e.target.value)}
                      placeholder="e.g. 15"
                    />
                  </div>
                  <div className="w-1/3">
                    <Select
                      label="Unit"
                      value={customUnit}
                      onChange={(e) => setCustomUnit(e.target.value)}
                    >
                      <option value="minutes">Minutes</option>
                      <option value="hours">Hours</option>
                    </Select>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <Button onClick={handleSave} loading={saving}>
                Save Settings
              </Button>
            </div>
          </div>

          {updatedAt && (
            <div className="flex items-center gap-1.5 text-xs text-slate-400 mt-2">
              <Clock size={12} />
              Last updated: {updatedAt}
            </div>
          )}
        </div>
      )}
    </CollapsibleSettingsCard>
  );
}
